import type {
  EChartsOption,
  MapSeriesOption,
  ScatterSeriesOption,
} from 'echarts';
import tinyColor from 'tinycolor2';

import { getMapName, toEChartsProjection } from '../geo/regionMaps';
import { getRegion } from '../geo/regions';
import {
  FONT_SIZE,
  FormatMetric,
  toBaseOption,
  toTooltipHtml,
  toTooltipStyle,
} from './shared';

// How faint a drill-down leaves the regions it didn't pick
const DESELECTED_OPACITY = 0.25;
// A bubble's width, from the least value (none) to the most
const MIN_BUBBLE_SIZE = 4;
const MAX_BUBBLE_SIZE = 36;
// Room under the map for its colour scale, or its note of what's left off
const FOOTER_HEIGHT = 36;
// The terms named in the note of what's left off, before "and N more"
const UNMATCHED_SHOWN = 3;
// The second dimension's terms a region's tooltip lists, before "and N more"
const BREAKDOWN_SHOWN = 8;

// A bubble's size, by the square root of its value: its area, not its
// width, follows its value
export const toMapBubbleSize = ({
  value,
  max,
}: {
  value: number;
  max: number;
}) =>
  max <= 0
    ? MIN_BUBBLE_SIZE
    : MIN_BUBBLE_SIZE +
      (MAX_BUBBLE_SIZE - MIN_BUBBLE_SIZE) * Math.sqrt(Math.max(0, value) / max);

// What's left off the map, as the note under it says it
export const toUnmatchedNote = (unmatched: string[]) => {
  if (unmatched.length === 0) {
    return '';
  }
  const shown = unmatched.slice(0, UNMATCHED_SHOWN).join(', ');
  const rest = unmatched.length - UNMATCHED_SHOWN;
  return `Not on the map: ${shown}${rest > 0 ? ` and ${rest} more` : ''}`;
};

// The shades values run through, from the least to the most: from just
// off an empty region's colour, through the metric's, to beyond it (darker
// in light mode, lighter in dark), so the least still reads against regions
// with no value and the most stands out
export const toShadeScale = ({
  color,
  theme,
}: {
  color: string;
  theme: StudioCharts.Theme;
}): string[] => [
  tinyColor.mix(theme.track, color, 20).toHexString(),
  tinyColor(color).toHexString(),
  tinyColor.mix(color, theme.textPrimary, 40).toHexString(),
];

// The shade a share of the way along the scale (0 the least, 1 the most)
export const toShadeAt = ({
  scale,
  share,
}: {
  scale: string[];
  share: number;
}): string => {
  const position = Math.min(1, Math.max(0, share)) * (scale.length - 1);
  const index = Math.min(scale.length - 2, Math.floor(position));
  return tinyColor
    .mix(scale[index], scale[index + 1], (position - index) * 100)
    .toHexString();
};

// A region's tooltip rows under its value: its value by the second
// dimension's terms, the most first, the rest only counted (not summed: the
// metric may not add up)
export const toBreakdownRows = ({
  breakdown,
  format,
}: {
  breakdown: StudioCharts.MapBreakdownRow[];
  format: (value: number | null) => string;
}): { name: string; value: string; note?: string }[] => {
  const rest = breakdown.length - BREAKDOWN_SHOWN;
  return breakdown.slice(0, BREAKDOWN_SHOWN).map((row, index, shown) => ({
    name: row.name,
    value: format(row.value),
    ...(rest > 0 && index === shown.length - 1
      ? { note: `and ${rest} more` }
      : {}),
  }));
};

const isDeselected = ({
  model,
  region,
}: {
  model: StudioCharts.RegionMapModel;
  region: StudioCharts.MapRegion;
}) => !!model.selectedKeys && !model.selectedKeys.includes(region.key);

// A Map: the area's states and provinces, each shaded by its value on a
// scale under the map (or with a bubble sized by it at its middle), those
// without a value left pale. A drill-down fades the regions it didn't pick.
const toRegionMapOption = ({
  model,
  theme,
  formatMetric,
}: {
  model: StudioCharts.RegionMapModel;
  theme: StudioCharts.Theme;
  formatMetric: FormatMetric;
}): EChartsOption => {
  const values = model.regions
    .map((r) => r.value)
    .filter((v): v is number => v !== null);
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  const isFill = model.drawAs === 'fill';
  const note = toUnmatchedNote(model.unmatched);
  const hasScale = isFill && model.showLegend;
  const format = (value: number | null) =>
    formatMetric({ metricId: model.metricId, value });

  // The outlines as both kinds draw them: edged in the background's
  // colour, as the heatmap's cells, pale where there's no value
  const outlines = {
    map: getMapName(model.area),
    projection: toEChartsProjection(model.area),
    left: 8,
    right: 8,
    top: 8,
    bottom: hasScale || note ? FOOTER_HEIGHT : 8,
    roam: false,
    selectedMode: false as const,
    itemStyle: {
      areaColor: theme.track,
      borderColor: theme.background,
      borderWidth: 0.75,
    },
    label: { show: false },
  };
  const scale = toShadeScale({ color: model.color, theme });
  const toShade = (value: number) =>
    toShadeAt({ scale, share: max === min ? 1 : (value - min) / (max - min) });

  // Each region shaded itself (not by the scale's visual map), so it keeps
  // its shade under the pointer, only its edge drawn darker
  const fill: MapSeriesOption = {
    type: 'map',
    id: model.seriesId,
    name: model.name,
    ...outlines,
    data: model.regions.map((region) => {
      const shade = region.value === null ? undefined : toShade(region.value);
      return {
        name: region.id,
        value: region.value ?? undefined,
        itemStyle: {
          ...(shade ? { areaColor: shade } : {}),
          ...(isDeselected({ model, region })
            ? { opacity: DESELECTED_OPACITY }
            : {}),
        },
        emphasis: {
          itemStyle: {
            ...(shade ? { areaColor: shade } : {}),
            borderColor: theme.textPrimary,
            borderWidth: 1.5,
          },
        },
      };
    }),
    emphasis: {
      label: { show: false },
      itemStyle: {
        areaColor: theme.track,
        borderColor: theme.textPrimary,
        borderWidth: 1.5,
      },
    },
  };
  const bubbles: ScatterSeriesOption = {
    type: 'scatter',
    id: model.seriesId,
    name: model.name,
    coordinateSystem: 'geo',
    geoIndex: 0,
    data: model.regions.flatMap((region) => {
      const centroid = getRegion(region.id)?.centroid;
      return centroid && region.value !== null
        ? [
            {
              name: region.id,
              value: [...centroid, region.value],
              symbolSize: toMapBubbleSize({ value: region.value, max }),
              ...(isDeselected({ model, region })
                ? { itemStyle: { opacity: DESELECTED_OPACITY } }
                : {}),
            },
          ]
        : [];
    }),
    itemStyle: {
      color: model.color,
      opacity: 0.85,
      borderColor: theme.background,
      borderWidth: 1,
    },
    emphasis: { scale: 1.1, itemStyle: { opacity: 1 } },
  };

  return {
    ...toBaseOption(theme),
    // Bubbles sit on the outlines, which don't respond to the pointer
    ...(isFill
      ? {}
      : {
          geo: {
            ...outlines,
            silent: true,
            emphasis: { disabled: true },
            tooltip: { show: false },
          },
        }),
    // The shades' scale, drawn as a key only: the regions shade themselves
    ...(hasScale
      ? {
          visualMap: {
            type: 'continuous',
            seriesIndex: [],
            min,
            max: max === min ? min + 1 : max,
            calculable: false,
            orient: 'horizontal',
            left: 0,
            bottom: 0,
            itemWidth: 10,
            itemHeight: 120,
            text: [format(max), format(min)],
            inRange: { color: scale },
            textStyle: { color: theme.textMuted, fontSize: FONT_SIZE },
          },
        }
      : {}),
    tooltip: {
      ...toTooltipStyle(theme),
      trigger: 'item',
      formatter: (params) => {
        const item = Array.isArray(params) ? params[0] : params;
        const region =
          item && item.name
            ? model.regions.find((r) => r.id === item.name)
            : undefined;
        const name = region
          ? region.name
          : (item && item.name && getRegion(item.name)?.name) || '';
        return name
          ? toTooltipHtml({
              title: name,
              rows: [
                {
                  name: model.name,
                  value: region ? format(region.value) : 'No value',
                },
                ...(region && region.breakdown
                  ? toBreakdownRows({ breakdown: region.breakdown, format })
                  : []),
              ],
            })
          : '';
      },
    },
    ...(note
      ? {
          graphic: [
            {
              type: 'text',
              right: 0,
              bottom: 2,
              silent: true,
              style: {
                text: note,
                fill: theme.textMuted,
                fontSize: FONT_SIZE,
                fontFamily: theme.fontFamily,
              },
            },
          ],
        }
      : {}),
    series: [isFill ? fill : bubbles],
  };
};

export default toRegionMapOption;
